import React from 'react';
import styled, { x } from '@xstyled/styled-components';
import { Item, registry, RenderProps } from '../Item';
import { FBPOST } from '../itemIds';
import { Flex } from '~/ui/flex/Flex';
import { Card } from '~/ui/card/Card';
import verifiedImage from './Verified.svg';
import mapImage from './Map.svg';
import postActionsImage from './PostActions.svg';
import reactionsImage from './Reactions.svg';
import { t } from 'ttag';
import { useEditorStore } from '~/shell/Store';
import { handlePaste } from '../helper/eventForContentEditable';
import useIsDarkMode from '~/lib/themes/hooks/useIsDarkMode';

interface Props {
  avatar?: string;
  pic?: string;
  name?: string;
  title?: string;
  body?: string;
}

const MapImg = styled(mapImage)`
  width: 11px;
  height: 11px;
  margin-left: 5px;
`;

const Verified = styled(verifiedImage)`
  width: 11px;
  height: 11px;
  margin-left: 5px;
`;

const PostActions = styled(postActionsImage)`
  margin-left: auto;
  margin-right: auto;
  margin-top: 8px;
  margin-bottom: 8px;
`;

const Reactions = styled(reactionsImage)`
  float: left;
  margin-top: 6px;
  margin-left: 10px;
  margin-right: 10px;
`;

const UserBlock = styled.div`
  margin-left: 10px;
  margin-top: 8px;
  font-family: sans-serif;
  font-style: normal;
  b {
    display: block;
    font-weight: 600;
    font-size: 14px;
    line-height: 11px;
    display: flex;
    align-items: flex-end;
    letter-spacing: -0.02em;
    color: black;
    margin: 3px;
    margin-top: 10px;
  }
  span {
    display: block;
    font-weight: 400;
    font-size: 10px;
    line-height: 14px;
    display: flex;
    align-items: flex-end;
    letter-spacing: -0.005em;
    color: #65676b;
  }
`;

const BodyBlock = styled.div`
  font-family: sans-serif;
  font-style: normal;
  font-weight: 400;
  font-size: 16px;
  line-height: 22px;
  display: flex;
  /* align-items: flex-end; */
  letter-spacing: -0.013em;
  color: black;
  padding: 15px 20px;
  flex-direction: column;
  align-items: self-start;

  &[contenteditable='true'] {
    position: relative;
    border: 2px solid transparent;

    span {
      max-width: 360px;
    }
    &:focus:after {
      opacity: 0;
    }
    &:hover {
      border: 2px solid #9738ff;
    }
  }
`;

const ReactionsBlock = styled.div`
  border-bottom: 1px solid #e5e5e5;
  margin-left: 8px;
  margin-right: 8px;
  padding-bottom: 6px;
  padding-top: 6px;
`;

const HeaderBlock = styled.div`
  height: 25px;
  display: flex;
  align-items: flex-end;
  letter-spacing: -0.016em;
  color: white;
  border-bottom: 1px solid #d8dadd;
  padding: 10px;
  padding-left: 14px;
`;

const GreyBlock = styled.div`
  background-color: greyLight;
  padding: 15px;
  padding-left: 20px;
  b {
    display: block;
    font-family: sans-serif;
    font-style: normal;
    font-weight: 700;
    font-size: 14px;
    line-height: 124.6%;
    letter-spacing: -0.032em;
    color: black;

    &[contenteditable='true'] {
      position: relative;
      border: 2px solid transparent;

      &:focus:after {
        opacity: 0;
      }

      &:hover {
        border: 2px solid #9738ff;
      }
    }
  }
  button {
    float: right;
    padding: 5px 8px;
    gap: 32px;
    width: 80px;
    height: 30px;
    background: #e4e6eb;
    border-radius: 4px;
    margin-top: 5px;

    font-family: sans-serif;
    font-style: normal;
    font-weight: 600;
    font-size: 10.4763px;
    line-height: 124.6%;
    text-align: right;
    letter-spacing: -0.03em;
    color: #000000;
    text-align: center;
  }
`;

const decodeHtmlEntities = (text: string) => {
  const textarea = document.createElement('textarea');
  textarea.innerHTML = text;
  return textarea.value;
};

export class Fbpost extends Item<typeof FBPOST, Props> {
  protected render({ avatar, pic, name, title, body, id }: RenderProps<Props>) {
    const { editorMode, updateSequence } = useEditorStore();
    const decodedTitle = title ? decodeHtmlEntities(title) : '';
    const isDarkMode = useIsDarkMode();

    const handleBlur = (
      fieldType: string,
      event: React.FocusEvent<HTMLElement>
    ) => {
      let newText = event.target.innerHTML;
      newText = newText.replace(/<div\s*><br\s*><\/div\s*>/gi, '<br>');

      let cleanText = newText.replace(/<div><br><\/div>/g, '<br>');
      cleanText = cleanText.replace(/<div>/g, '<br>');
      cleanText = cleanText.replace(/<\/div><br>/g, '<br>');
      cleanText = cleanText.replace(/<div>/g, '\n').replace(/<\/div>/g, '');

      if (!newText.startsWith('<div>')) {
        const firstDivIndex = cleanText.indexOf('\n');
        if (firstDivIndex !== -1) {
          cleanText =
            cleanText.slice(0, firstDivIndex) +
            '\n' +
            cleanText.slice(firstDivIndex + 1);
        }
      }

      if (cleanText.trim() == '') {
        cleanText = 'Type Here';
        event.target.innerHTML = cleanText;
      }

      updateSequence(id, fieldType, cleanText);
    };

    const handleInput = (event: React.ChangeEvent<HTMLDivElement>) => {
      let newText = event.currentTarget.textContent;
      const textWithoutHtml = newText?.replace(/<[^>]*>/g, '');

      if (textWithoutHtml?.trim() == '') {
        newText = ' ';
        event.currentTarget.textContent = newText;

        const range = document.createRange();
        const selection = window.getSelection();
        range.selectNodeContents(event.currentTarget);
        range.collapse(false);
        selection?.removeAllRanges();
        selection?.addRange(range);
      }
    };

    const handleFocus = (event: React.FocusEvent<HTMLDivElement>) => {
      if (
        editorMode &&
        event.currentTarget.textContent?.toLowerCase() === 'type here'
      ) {
        const element = event.currentTarget;
        element.textContent = '';
        element.focus();

        setTimeout(() => {
          const range = document.createRange();
          const selection = window.getSelection();
          range.selectNodeContents(element);
          range.collapse(false);
          selection?.removeAllRanges();
          selection?.addRange(range);
        }, 0);
      }
    };

    return (
      <Flex variant="center">
        <Card
          backgroundColor="white"
          w={{ _: '90%', sm: '400px' }}
          p="0px"
          display="flex"
          flexDirection="column"
          position="relative"
        >
          <HeaderBlock />
          <UserBlock>
            {avatar && (
              <x.img
                src={avatar}
                h="40px"
                w="40px"
                borderRadius="50%"
                marginRight="10px"
                float="left"
                alt={t`picture avatar`}
                style={{
                  backgroundColor: isDarkMode ? '#FFF' : 'transparent'
                }}
              />
            )}
            <b className="userName">
              {name} <Verified />
            </b>
            <span>
              2h ago <MapImg />
            </span>
          </UserBlock>
          <BodyBlock
            contentEditable={editorMode}
            dangerouslySetInnerHTML={{ __html: body || '' }}
            onBlur={editorMode ? e => handleBlur('body', e) : undefined}
            onInput={editorMode ? handleInput : undefined}
            onPaste={editorMode ? handlePaste : undefined}
            onFocus={editorMode ? handleFocus : undefined}
            suppressContentEditableWarning={true}
          />
          {pic && <x.img src={pic} alt={t`picture`} />}
          <GreyBlock>
            <b
              contentEditable={editorMode}
              onBlur={editorMode ? e => handleBlur('title', e) : undefined}
              onInput={editorMode ? handleInput : undefined}
              onPaste={editorMode ? handlePaste : undefined}
              onFocus={editorMode ? handleFocus : undefined}
              suppressContentEditableWarning={true}
            >
              {decodedTitle}
            </b>
          </GreyBlock>
          <ReactionsBlock>
            <Reactions />
            <x.span
              float="right"
              fontSize="10"
              color="#65676B"
              style={{ marginRight: '15px' }}
            >
              20 Comments 8 Shares
            </x.span>
          </ReactionsBlock>
          <PostActions />
        </Card>
      </Flex>
    );
  }
}

registry[FBPOST] = Fbpost;
